import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../context/AuthContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt     = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'
const money   = (n) => n != null ? `${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RWF` : '—'
const moneyN  = (n) => Number(n || 0).toFixed(2)

const netFee  = (gross, pct) => +(Number(gross  || 0) * (1 - (pct || 0) / 100)).toFixed(2)
const commAmt = (gross, pct) => +(Number(gross  || 0) * ((pct  || 0) / 100)).toFixed(2)

const avatarBg = (str = '') => {
  const c = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']
  let h = 0; for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return c[Math.abs(h) % c.length]
}
const initials = (n = '') => (n.trim().charAt(0) || '?').toUpperCase()

const STATUS_MAP = {
  1: { bg: '#f0fdf4', color: '#15803d', dot: '#16a34a', label: 'Active'    },
  0: { bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444', label: 'Inactive'  },
  2: { bg: '#fffbeb', color: '#b45309', dot: '#f59e0b', label: 'Pending'   },
}
const sc = (s) => STATUS_MAP[Number(s)] ?? { bg: '#f3f4f6', color: '#6b7280', dot: '#9ca3af', label: '—' }

// ── CSV Export ────────────────────────────────────────────────────────────────
function exportDriverCSV(drivers, period, commPct = 0) {
  const escape  = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const hasComm = commPct > 0
  const headers = [
    'Driver Name','Phone','Company','Vehicle Type','Plate','Deliveries',
    'Gross Fee (RWF)',
    ...(hasComm ? [`Commission (${commPct}%)`, 'Commission (RWF)', 'Net Payout (RWF)'] : []),
    'Status',
  ]
  const lines = drivers.map(d => [
    d.name || '',
    d.phone || '',
    d.company_name || 'Independent',
    d.vehicle_type || '',
    d.vehicle_plate || '',
    d.delivery_count,
    moneyN(d.total_fee),
    ...(hasComm ? [`${commPct}%`, moneyN(commAmt(d.total_fee, commPct)), moneyN(netFee(d.total_fee, commPct))] : []),
    sc(d.status).label,
  ].map(escape).join(','))
  const csv  = [headers.join(','), ...lines].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a'); a.href = url
  a.download = `kudos-driver-payments-${period}.csv`; a.click()
  URL.revokeObjectURL(url)
}

function exportCompanyCSV(companies, period, commPct = 0) {
  const escape  = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const hasComm = commPct > 0
  const headers = [
    'Company','Drivers','Deliveries','Gross Fee (RWF)',
    ...(hasComm ? [`Commission (${commPct}%)`, 'Commission (RWF)', 'Net Payout (RWF)'] : []),
  ]
  const lines = companies.map(c => [
    c.company_name, c.driver_count, c.delivery_count, moneyN(c.total_fee),
    ...(hasComm ? [`${commPct}%`, moneyN(commAmt(c.total_fee, commPct)), moneyN(netFee(c.total_fee, commPct))] : []),
  ].map(escape).join(','))
  const csv  = [headers.join(','), ...lines].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a'); a.href = url
  a.download = `kudos-company-payments-${period}.csv`; a.click()
  URL.revokeObjectURL(url)
}

// ── Sub-components ────────────────────────────────────────────────────────────
function SummaryCard({ label, value, sub, accent, bold }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '16px 18px', flex: 1 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: bold ? 22 : 20, fontWeight: 800, letterSpacing: '-0.5px', color: accent || '#111317' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#9a9a94', marginTop: 4, fontFamily: "'JetBrains Mono',monospace" }}>{sub}</div>}
    </div>
  )
}

function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ height: 32, minWidth: 32, padding: '0 10px', borderRadius: 6, border: `1px solid ${active ? '#111317' : '#e9e9e4'}`, background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#d1d5db' : '#374151', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: "'JetBrains Mono',monospace" }}>
      {label}
    </button>
  )
}

// ── Driver Delivery Drawer ─────────────────────────────────────────────────────
function DriverDrawer({ driver, dateFrom, dateTo, onClose, commPct = 0 }) {
  const [deliveries, setDeliveries] = useState([])
  const [totalFee,   setTotalFee]   = useState(0)
  const [loading,    setLoading]    = useState(true)

  useEffect(() => {
    if (!driver) return
    setLoading(true)
    api.get(`/payments/admin/driver-report/${driver.driver_id}?date_from=${dateFrom}&date_to=${dateTo}`)
      .then(res => {
        setDeliveries(res.data.deliveries || [])
        setTotalFee(res.data.total_fee || 0)
      })
      .catch(() => { setDeliveries([]); setTotalFee(0) })
      .finally(() => setLoading(false))
  }, [driver, dateFrom, dateTo])

  if (!driver) return null

  const bg = avatarBg(driver.name || '')

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.18)', zIndex: 200 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 500, background: '#fff', zIndex: 201, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 40px rgba(0,0,0,0.12)', animation: 'slideIn 0.22s ease' }}>
        <style>{`@keyframes slideIn{from{transform:translateX(40px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>

        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, flexShrink: 0 }}>{initials(driver.name)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{driver.name || '—'}</div>
            <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>
              {driver.phone} · {driver.vehicle_type || 'Unknown vehicle'} · {driver.company_name || 'Independent'}
            </div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 22, color: '#9a9a94', lineHeight: 1 }}>×</div>
        </div>

        {/* Payout summary strip */}
        <div style={{ padding: '14px 22px', borderBottom: '1px solid #e9e9e4', background: '#f0fdf4' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: commPct > 0 ? 10 : 0 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace" }}>
                Delivery Payout · {fmt(dateFrom)} – {fmt(dateTo)}
              </div>
              <div style={{ fontSize: 11, color: '#15803d', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>
                {deliveries.length} completed deliveries
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              {commPct > 0 && (
                <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textDecoration: 'line-through', marginBottom: 1 }}>
                  {money(totalFee)}
                </div>
              )}
              <div style={{ fontSize: 22, fontWeight: 900, color: '#15803d', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '-0.03em' }}>
                {money(commPct > 0 ? netFee(totalFee, commPct) : totalFee)}
              </div>
              {commPct > 0 && <div style={{ fontSize: 9, color: '#15803d', fontFamily: "'JetBrains Mono',monospace" }}>net to driver</div>}
            </div>
          </div>
          {commPct > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {[
                { lbl: 'Gross Earned',           val: money(totalFee),                       color: '#374151' },
                { lbl: `Commission (${commPct}%)`, val: `−${money(commAmt(totalFee, commPct))}`, color: '#b91c1c' },
                { lbl: 'Net Payout',             val: money(netFee(totalFee, commPct)),      color: '#15803d' },
              ].map(({ lbl, val, color }) => (
                <div key={lbl} style={{ background: 'rgba(255,255,255,0.65)', borderRadius: 7, padding: '7px 10px', border: '1px solid rgba(0,0,0,0.05)' }}>
                  <div style={{ fontSize: 8, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 3 }}>{lbl}</div>
                  <div style={{ fontSize: 11, fontWeight: 800, color, fontFamily: "'JetBrains Mono',monospace" }}>{val}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Deliveries list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading deliveries…</div>
          ) : deliveries.length === 0 ? (
            <div style={{ padding: '48px 22px', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No deliveries in this period.</div>
          ) : (
            <>
              {/* Table header */}
              <div style={{ display: 'grid', gridTemplateColumns: '0.6fr 1.6fr 1.2fr 1fr 0.9fr', padding: '10px 22px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
                {['#', 'Buyer / Shop', 'Delivered At', 'Order Total', commPct > 0 ? 'Net Fee' : 'Delivery Fee'].map(h => (
                  <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
                ))}
              </div>

              {deliveries.map((d, i) => (
                <div key={d.order_id || i} style={{ display: 'grid', gridTemplateColumns: '0.6fr 1.6fr 1.2fr 1fr 0.9fr', padding: '11px 22px', borderBottom: i < deliveries.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center' }}>
                  <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{d.order_id}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.buyer_name || 'Anonymous'}</div>
                    <div style={{ fontSize: 10, color: '#9a9a94', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.shop_name || '—'}</div>
                  </div>
                  <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmtTime(d.delivered_at || d.created_at)}</div>
                  <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{moneyN(d.total_amount)}</div>
                  <div>
                    <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, color: '#15803d' }}>
                      {moneyN(commPct > 0 ? netFee(d.delivery_fee, commPct) : d.delivery_fee)}
                    </div>
                    {commPct > 0 && Number(d.delivery_fee) > 0 && (
                      <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                        {moneyN(d.delivery_fee)} −{commPct}%
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Footer total */}
              <div style={{ display: 'grid', gridTemplateColumns: '0.6fr 1.6fr 1.2fr 1fr 0.9fr', padding: '12px 22px', borderTop: '2px solid #e9e9e4', background: '#f0fdf4', alignItems: 'center' }}>
                <div /><div style={{ fontSize: 11, fontWeight: 800, color: '#15803d' }}>Total ({deliveries.length})</div>
                <div /><div />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>
                    {moneyN(commPct > 0 ? netFee(totalFee, commPct) : totalFee)}
                  </div>
                  {commPct > 0 && (
                    <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                      gross {moneyN(totalFee)}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DriverPayment() {
  const now        = new Date()
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  const monthEnd   = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10)

  const [drivers,      setDrivers]      = useState([])
  const [companies,    setCompanies]    = useState([])
  const [summary,      setSummary]      = useState({ driver_count: 0, active_driver_count: 0, delivery_count: 0, total_fee: 0 })
  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState('')
  const [dateFrom,     setDateFrom]     = useState(monthStart)
  const [dateTo,       setDateTo]       = useState(monthEnd)
  const [searchInput,  setSearchInput]  = useState('')
  const [search,       setSearch]       = useState('')
  const [companyFilter,setCompanyFilter]= useState('')
  const [tab,          setTab]          = useState('drivers')
  const [page,         setPage]         = useState(1)
  const [totalPages,   setTotalPages]   = useState(1)
  const [totalCount,   setTotalCount]   = useState(0)
  const [selected,     setSelected]     = useState(null)
  const [exporting,    setExporting]    = useState(false)
  const [commPct,      setCommPct]      = useState(0)
  const fetchIdRef = useRef(0)

  useEffect(() => {
    api.get('/settings/admin/config')
      .then(res => setCommPct(Number(res.data.config?.driver_commission_pct) || 0))
      .catch(() => {})
  }, [])

  const PER_PAGE = 25

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  const fetchReport = useCallback(async () => {
    const id = ++fetchIdRef.current
    setLoading(true); setError('')
    try {
      const params = new URLSearchParams({
        page, limit: PER_PAGE,
        date_from: dateFrom, date_to: dateTo,
        ...(search        && { search }),
        ...(companyFilter && { company: companyFilter }),
      })
      const res = await api.get(`/payments/admin/driver-report?${params}`)
      if (id !== fetchIdRef.current) return
      setDrivers(res.data.drivers     || [])
      setCompanies(res.data.companies || [])
      setSummary(res.data.summary     || { driver_count: 0, active_driver_count: 0, delivery_count: 0, total_fee: 0 })
      setTotalPages(res.data.totalPages || 1)
      setTotalCount(res.data.total      || 0)
    } catch {
      if (id !== fetchIdRef.current) return
      setError('Failed to load driver payment report.')
    } finally {
      if (id === fetchIdRef.current) setLoading(false)
    }
  }, [page, dateFrom, dateTo, search, companyFilter])

  useEffect(() => { fetchReport() },                         [fetchReport])
  useEffect(() => { setPage(1) },                            [dateFrom, dateTo, search, companyFilter])

  const handleExportCSV = async () => {
    setExporting(true)
    try {
      const params = new URLSearchParams({ page: 1, limit: 9999, date_from: dateFrom, date_to: dateTo, ...(search && { search }), ...(companyFilter && { company: companyFilter }) })
      const res = await api.get(`/payments/admin/driver-report?${params}`)
      if (tab === 'companies') {
        exportCompanyCSV(res.data.companies || companies, `${dateFrom}_${dateTo}`, commPct)
      } else {
        exportDriverCSV(res.data.drivers || drivers, `${dateFrom}_${dateTo}`, commPct)
      }
    } catch {
      if (tab === 'companies') exportCompanyCSV(companies, `${dateFrom}_${dateTo}`, commPct)
      else exportDriverCSV(drivers, `${dateFrom}_${dateTo}`, commPct)
    } finally {
      setExporting(false)
    }
  }

  const periodLabel  = `${fmt(dateFrom)} – ${fmt(dateTo)}`
  const grossTotal   = summary.total_fee
  const commTotal    = commAmt(grossTotal, commPct)
  const netTotal     = netFee(grossTotal, commPct)
  const avgNet       = summary.active_driver_count > 0 ? netTotal / summary.active_driver_count : 0

  // Unique companies for filter dropdown (from full company list)
  const companyOptions = companies.map(c => c.company_name).filter(n => n !== 'Independent')

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Driver Delivery Payments</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Delivery fee breakdown per driver and company for bank transfer.</div>
        </div>
        <button
          onClick={handleExportCSV}
          disabled={exporting || (drivers.length === 0 && companies.length === 0)}
          style={{ height: 38, padding: '0 16px', borderRadius: 8, border: 'none', background: exporting ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: exporting ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 7, boxShadow: '0 2px 8px rgba(232,68,10,0.2)' }}>
          {exporting ? '⏳ Exporting…' : '⬇ Export CSV'}
        </button>
      </div>

      {/* Filters row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '12px 16px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>From</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 10px', fontSize: 12, fontWeight: 600, color: '#111317', outline: 'none', fontFamily: "'JetBrains Mono',monospace" }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>To</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 10px', fontSize: 12, fontWeight: 600, color: '#111317', outline: 'none', fontFamily: "'JetBrains Mono',monospace" }} />
        </div>

        {/* Month shortcuts */}
        {[0, 1, 2].map(monthsAgo => {
          const d    = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1)
          const from = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`
          const to   = new Date(d.getFullYear(), d.getMonth()+1, 0).toISOString().slice(0,10)
          const lbl  = monthsAgo === 0 ? 'This month' : d.toLocaleString('en-GB', { month: 'short', year: 'numeric' })
          const active = dateFrom === from && dateTo === to
          return (
            <button key={monthsAgo} onClick={() => { setDateFrom(from); setDateTo(to) }}
              style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${active ? '#111317' : '#e9e9e4'}`, background: active ? '#111317' : '#fff', color: active ? '#fff' : '#6b7280', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              {lbl}
            </button>
          )
        })}

        <div style={{ flex: 1 }} />

        {/* Search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 11px' }}>
          <span style={{ fontSize: 13, color: '#9a9a94' }}>🔍</span>
          <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Driver name, phone…"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, width: 160, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
          {searchInput && <span onClick={() => { setSearchInput(''); setSearch('') }} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 14 }}>×</span>}
        </div>

        {/* Company filter */}
        <select value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}
          style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 11px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          <option value="">All Companies</option>
          <option value="__independent__">Independent</option>
          {companyOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Period label */}
      <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginBottom: 16 }}>
        Report period: <b style={{ color: '#111317' }}>{periodLabel}</b> · {totalCount.toLocaleString()} drivers
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <SummaryCard
          label="Net to Pay Drivers"
          value={money(netTotal)}
          accent="#15803d" bold
          sub={commPct > 0 ? `after ${commPct}% commission · gross ${money(grossTotal)}` : 'sum of all delivery fees'}
        />
        {commPct > 0 && (
          <SummaryCard
            label={`Platform Commission (${commPct}%)`}
            value={money(commTotal)}
            accent="#e8440a"
            sub={`from ${money(grossTotal)} gross delivery fees`}
          />
        )}
        <SummaryCard label="Drivers with Deliveries" value={summary.active_driver_count}            accent="#111317" sub={`of ${summary.driver_count} total drivers`} />
        <SummaryCard label="Total Deliveries"        value={summary.delivery_count.toLocaleString()} accent="#374151" sub="completed orders" />
        <SummaryCard label="Avg Net / Driver"        value={money(avgNet)}                           accent="#b45309" sub={commPct > 0 ? 'net after commission' : 'delivery fees / driver'} />
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: 5, width: 'fit-content' }}>
        {[
          ['drivers',   `🚗 By Driver (${totalCount.toLocaleString()})`],
          ['companies', `🏢 By Company (${companies.length})`],
        ].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding: '7px 20px', borderRadius: 7, border: 'none', background: tab === id ? '#111317' : 'transparent', color: tab === id ? '#fff' : '#9a9a94', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'all 0.15s' }}>
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#b91c1c' }}>⚠ {error}</div>
      )}

      {/* ── By Driver Tab ── */}
      {tab === 'drivers' && (
        <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.2fr 1.4fr 1fr 0.8fr 1.1fr 0.7fr', padding: '10px 18px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
            {['Driver', 'Phone', 'Company / Vehicle', 'Plate', 'Deliveries', commPct > 0 ? 'Gross → Net' : 'Delivery Fee', 'Status'].map(h => (
              <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
            ))}
          </div>

          {loading ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading driver report…</div>
          ) : drivers.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No drivers found for this period.</div>
          ) : (
            <>
              {drivers.map((d, i) => {
                const bg = avatarBg(d.name || '')
                const s  = sc(d.status)
                return (
                  <div key={d.driver_id || i}
                    onClick={() => setSelected(d)}
                    style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.2fr 1.4fr 1fr 0.8fr 1.1fr 0.7fr', padding: '12px 18px', borderBottom: i < drivers.length - 1 ? '1px solid #f3f3f0' : 'none', cursor: 'pointer', alignItems: 'center', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fafaf8'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    {/* Driver name + avatar */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <div style={{ width: 34, height: 34, borderRadius: 8, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{initials(d.name)}</div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name || '—'}</div>
                        <div style={{ fontSize: 10, color: '#9a9a94' }}>ID #{d.driver_id}</div>
                      </div>
                    </div>

                    {/* Phone */}
                    <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>{d.phone || '—'}</div>

                    {/* Company / Vehicle */}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.company_name || <span style={{ color: '#d1d5db', fontStyle: 'italic', fontWeight: 400 }}>Independent</span>}</div>
                      <div style={{ fontSize: 10, color: '#9a9a94' }}>{d.vehicle_type || '—'}</div>
                    </div>

                    {/* Plate */}
                    <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600, color: '#374151' }}>{d.vehicle_plate || '—'}</div>

                    {/* Deliveries */}
                    <div style={{ fontSize: 13, fontWeight: 700, color: Number(d.delivery_count) > 0 ? '#111317' : '#d1d5db', fontFamily: "'JetBrains Mono',monospace" }}>{d.delivery_count}</div>

                    {/* Fee */}
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: Number(d.total_fee) > 0 ? '#15803d' : '#9a9a94' }}>
                        {moneyN(commPct > 0 ? netFee(d.total_fee, commPct) : d.total_fee)}
                      </div>
                      {commPct > 0 && Number(d.total_fee) > 0 && (
                        <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                          {moneyN(d.total_fee)} −{commPct}%
                        </div>
                      )}
                    </div>

                    {/* Status badge */}
                    <div>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: s.bg, color: s.color, fontSize: 9, fontWeight: 700, padding: '3px 7px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: "'JetBrains Mono',monospace" }}>
                        <span style={{ width: 4, height: 4, borderRadius: '50%', background: s.dot }} />{s.label}
                      </span>
                    </div>
                  </div>
                )
              })}

              {/* Page totals footer */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.2fr 1.4fr 1fr 0.8fr 1.1fr 0.7fr', padding: '12px 18px', borderTop: '2px solid #e9e9e4', background: '#f9f9f7', alignItems: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#111317' }}>Page Total ({drivers.length})</div>
                <div /><div /><div />
                <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace" }}>
                  {drivers.reduce((s, d) => s + Number(d.delivery_count || 0), 0)}
                </div>
                <div>
                  {(() => {
                    const pageGross = drivers.reduce((s, d) => s + Number(d.total_fee || 0), 0)
                    return (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>
                          {moneyN(commPct > 0 ? netFee(pageGross, commPct) : pageGross)}
                        </div>
                        {commPct > 0 && (
                          <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                            gross {moneyN(pageGross)}
                          </div>
                        )}
                      </>
                    )
                  })()}
                </div>
                <div />
              </div>
            </>
          )}
        </div>
      )}

      {/* ── By Company Tab ── */}
      {tab === 'companies' && (
        <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.2fr', padding: '10px 18px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
            {['Company', 'Drivers', 'Deliveries', commPct > 0 ? 'Gross → Net' : 'Total Delivery Fee'].map(h => (
              <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
            ))}
          </div>

          {loading ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading…</div>
          ) : companies.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No company data available.</div>
          ) : (
            <>
              {companies.map((c, i) => {
                const isIndependent = c.company_name === 'Independent'
                const pct = summary.total_fee > 0 ? (Number(c.total_fee) / summary.total_fee * 100).toFixed(1) : 0
                return (
                  <div key={c.company_name || i}
                    style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.2fr', padding: '13px 18px', borderBottom: i < companies.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center' }}>
                    {/* Company name */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 8, background: isIndependent ? '#f3f4f6' : avatarBg(c.company_name), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, flexShrink: 0 }}>
                        {isIndependent ? '👤' : initials(c.company_name)}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{c.company_name}</div>
                        <div style={{ marginTop: 4, height: 4, width: 120, background: '#f3f4f6', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: '#15803d', borderRadius: 2 }} />
                        </div>
                        <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>{pct}% of total</div>
                      </div>
                    </div>

                    {/* Drivers */}
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', fontFamily: "'JetBrains Mono',monospace" }}>{c.driver_count}</div>

                    {/* Deliveries */}
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', fontFamily: "'JetBrains Mono',monospace" }}>{c.delivery_count}</div>

                    {/* Fee */}
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>
                        {moneyN(commPct > 0 ? netFee(c.total_fee, commPct) : c.total_fee)}
                      </div>
                      {commPct > 0 && (
                        <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                          {moneyN(c.total_fee)} −{commPct}% comm
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}

              {/* Footer */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.2fr', padding: '12px 18px', borderTop: '2px solid #e9e9e4', background: '#f9f9f7', alignItems: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#111317' }}>Grand Total</div>
                <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace" }}>{summary.driver_count}</div>
                <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace" }}>{summary.delivery_count}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>
                    {moneyN(commPct > 0 ? netTotal : summary.total_fee)}
                  </div>
                  {commPct > 0 && (
                    <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                      gross {moneyN(summary.total_fee)}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Pagination */}
      {tab === 'drivers' && totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
            Page {page} of {totalPages} · {totalCount.toLocaleString()} drivers
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <PagBtn label="← Prev" disabled={page === 1}          onClick={() => setPage(p => p - 1)} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
              return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} />
            })}
            <PagBtn label="Next →" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
          </div>
        </div>
      )}

      {/* Driver drawer */}
      {selected && (
        <DriverDrawer
          driver={selected}
          dateFrom={dateFrom}
          dateTo={dateTo}
          commPct={commPct}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}
